import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Home, LogOut, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';

const LogoutSuccess: React.FC = () => {
    const navigate = useNavigate();

    useEffect(() => {
        // Clear any local storage auth data
        localStorage.removeItem('hospitalstatus_auth');
        localStorage.removeItem('hospitalstatus_rooms');
        localStorage.removeItem('hospitalstatus_patients');
    }, []);

    return (
        <div className="min-h-screen bg-[#0a0c10] flex items-center justify-center px-6 relative overflow-hidden">
            {/* Background glow effects */}
            <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-1/3 right-1/4 w-[350px] h-[350px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="text-center max-w-md w-full"
            >
                {/* Success Icon */}
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
                    className="w-24 h-24 mx-auto mb-8 rounded-full bg-emerald-500/10 border-2 border-emerald-500/20 flex items-center justify-center"
                >
                    <CheckCircle className="w-12 h-12 text-emerald-500" />
                </motion.div>

                {/* Title */}
                <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-2xl md:text-3xl font-black text-white tracking-tight mb-3"
                >
                    Sesión Cerrada
                </motion.h1>

                {/* Description */}
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-white/40 text-sm leading-relaxed mb-10 max-w-xs mx-auto"
                >
                    Tu sesión se ha cerrado de forma segura. Todos los datos locales han sido eliminados de este dispositivo.
                </motion.p>

                {/* Action Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex flex-col gap-3"
                >
                    <button
                        onClick={() => navigate('/')}
                        className="w-full flex items-center justify-center gap-2.5 px-6 py-4 bg-gradient-to-r from-[#5B65DC] to-indigo-600 text-white font-bold rounded-2xl shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all text-sm"
                    >
                        <Home className="w-4 h-4" />
                        Ir a la Página Principal
                    </button>

                    <button
                        onClick={() => navigate('/login')}
                        className="w-full flex items-center justify-center gap-2.5 px-6 py-4 bg-white/5 text-white/60 font-bold rounded-2xl border border-white/10 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all text-sm"
                    >
                        <LogOut className="w-4 h-4" />
                        Iniciar Sesión de Nuevo
                    </button>
                </motion.div>

                {/* Security Badge */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                    className="mt-10 inline-flex items-center gap-2 px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-full"
                >
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-500/60" />
                    <span className="text-[10px] font-bold text-white/30 uppercase tracking-widest">
                        Sesión finalizada de forma segura
                    </span>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default LogoutSuccess;
